import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

const COUPON_CODE = "WELCOME5";
const STORAGE_KEY = "pv_exit_intent_seen";

export default function ExitIntentPopup() {
  const { lang } = useI18n();
  const nl = lang === "nl";
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    let armed = false;
    // Arm after 15s so we don't fire on immediate reload
    const armTimer = setTimeout(() => { armed = true; }, 15000);

    const onLeave = (e) => {
      if (!armed) return;
      if (e.clientY < 12 && !open) {
        setOpen(true);
        localStorage.setItem(STORAGE_KEY, "1");
      }
    };
    document.addEventListener("mouseleave", onLeave);
    return () => {
      document.removeEventListener("mouseleave", onLeave);
      clearTimeout(armTimer);
    };
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(COUPON_CODE);
      setCopied(true);
      toast.success(nl ? "Code gekopieerd!" : "Code copied!");
    } catch { /* noop */ }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="exit-intent"
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <motion.div
            initial={{ y: 40, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-[#F7F7F6] border border-black max-w-lg w-full overflow-hidden"
          >
            {/* Colored ribbon */}
            <div className="h-1.5 flex">
              <span className="flex-[3]" style={{ background: "#00529B" }} />
              <span className="flex-[2]" style={{ background: "#D12A2F" }} />
              <span className="flex-[3]" style={{ background: "#5A2A82" }} />
              <span className="flex-[2]" style={{ background: "#007A78" }} />
            </div>

            {/* Ambient glow */}
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{ background: "radial-gradient(circle at 50% 100%, rgba(0,82,155,0.18), transparent 55%)", filter: "blur(30px)" }}
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />

            <button
              data-testid="exit-close"
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 font-mono-label text-[10px] text-neutral-500 hover:text-black z-10"
            >
              CLOSE ×
            </button>

            <div className="relative px-8 py-10 md:px-12 md:py-14 text-center">
              <p className="font-mono-label text-[10px] tracking-[0.24em] text-[#00529B] mb-4">
                [ {nl ? "WACHT · EENMALIG AANBOD" : "WAIT · ONE-TIME OFFER"} ]
              </p>
              <h2 className="font-serif-display font-light text-4xl md:text-5xl leading-[1.02] tracking-tight">
                {nl ? "5% korting" : "5% off"}
                <span className="block italic text-neutral-500 text-2xl md:text-3xl mt-1">
                  {nl ? "op je eerste bestelling" : "your first order"}
                </span>
              </h2>
              <p className="mt-5 text-neutral-600 text-sm max-w-sm mx-auto">
                {nl
                  ? "Gebruik onderstaande code bij het afrekenen. Geldig op alle producten. Beperkte tijd."
                  : "Use the code below at checkout. Valid on all products. Limited time."}
              </p>

              <button
                onClick={copy}
                data-testid="exit-coupon"
                className="mt-8 group relative inline-flex items-center gap-4 border-2 border-dashed border-black bg-white px-6 py-4 hover:bg-black hover:text-white transition-colors"
              >
                <span className="font-mono-label text-[10px] text-neutral-500 group-hover:text-neutral-300">
                  {copied ? (nl ? "GEKOPIEERD" : "COPIED") : (nl ? "KLIK OM TE KOPIËREN" : "CLICK TO COPY")}
                </span>
                <span className="font-serif-display text-3xl md:text-4xl tracking-tight">{COUPON_CODE}</span>
              </button>

              <a
                href="/shop"
                data-testid="exit-shop"
                className="btn-solid mt-8 inline-flex"
              >
                {nl ? "Nu shoppen" : "Shop now"} →
              </a>

              <p className="mt-6 font-mono-label text-[9px] text-neutral-400">
                {nl ? "Eenmalig gebruik per klant. Kan niet worden gecombineerd." : "One use per customer. Cannot be combined."}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
